/**
 * Preserve dashboard filters (store, stylist, month) across navigations.
 * Month is never invented here — only copied from the current URL/search.
 */
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export const DASHBOARD_FILTER_KEYS = ['store', 'stylist', 'month'];

/**
 * @param {string} pathname - path, optionally with its own query (own query wins per-key)
 * @param {URLSearchParams|string|null|undefined} search - current location search
 * @returns {string} path + filter query
 */
export function withDashboardFilters(pathname, search) {
  const [pathOnly, pathQs] = String(pathname || '/').split('?');
  const fromPath = new URLSearchParams(pathQs || '');
  const fromSearch =
    search instanceof URLSearchParams
      ? search
      : new URLSearchParams(typeof search === 'string' ? search.replace(/^\?/, '') : '');

  // Keep path-supplied params (e.g. ?edit=kpi2), then overlay store/stylist/month.
  const next = new URLSearchParams(fromPath);
  for (const key of DASHBOARD_FILTER_KEYS) {
    const v = fromPath.get(key) || fromSearch.get(key);
    if (v) next.set(key, v);
  }
  const qs = next.toString();
  return qs ? `${pathOnly}?${qs}` : pathOnly;
}

/** Settings gear path with current filters. */
export function getSettingsPath(search) {
  return withDashboardFilters('/settings', search);
}

const NO_FILTER_PREFIXES = ['/login', '/forgot-password', '/invite', '/admin/iam'];

function shouldCarryFilters(pathname) {
  const path = String(pathname || '').split('?')[0];
  return !NO_FILTER_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

/** navigate() that carries store/stylist/month on dashboard routes. */
export function useNavigateWithFilters() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  return useCallback(
    (to, opts) => {
      if (typeof to === 'string' && shouldCarryFilters(to)) {
        navigate(withDashboardFilters(to, searchParams), opts);
      } else {
        navigate(to, opts);
      }
    },
    [navigate, searchParams]
  );
}
